import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBlogs } from '../../services/blogService';
import { getAllComments } from '../../services/commentService';
import { getAllLikes } from '../../services/likeService';
import StatCard from '../../components/admin/StatCard';
import StatusBadge from '../../components/admin/StatusBadge';
import CardSkeleton from '../../components/admin/CardSkeleton';
import AdminAnalytics from './AdminAnalytics';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPublished: 0,
    totalDrafts: 0,
    pendingComments: 0,
    totalLikes: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [blogs, comments, likes] = await Promise.all([
        getAllBlogs(),
        getAllComments(),
        getAllLikes()
      ]);

      // Calculate stats
      const publishedBlogs = blogs.filter(blog => blog.status === 'published');
      const draftBlogs = blogs.filter(blog => blog.status === 'draft');
      const pendingComments = comments.filter(comment => comment.status === 'pending');

      // Calculate total likes across all blogs
      const totalLikes = likes.length;

      setStats({
        totalPublished: publishedBlogs.length,
        totalDrafts: draftBlogs.length,
        pendingComments: pendingComments.length,
        totalLikes
      });

      // Get recent 5 posts
      const recent = blogs.slice(0, 5);
      setRecentPosts(recent);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="dashboard-grid mb-4">
          {[...Array(4)].map((_, i) => (
            <CardSkeleton key={i} height="120px" />
          ))}
        </div>
        <CardSkeleton height="300px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error Loading Dashboard</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={fetchDashboardData}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      <div className="dashboard-grid mb-4">
        <StatCard
          title="Published Posts"
          value={stats.totalPublished}
          icon="bi-file-earmark-check"
          color="success"
        />
        <StatCard
          title="Draft Posts"
          value={stats.totalDrafts}
          icon="bi-pencil-square"
          color="warning"
        />
        <StatCard
          title="Pending Comments"
          value={stats.pendingComments}
          icon="bi-chat-dots"
          color="info"
        />
        <StatCard
          title="Total Likes"
          value={stats.totalLikes}
          icon="bi-heart"
          color="danger"
        />
      </div>

      {/* Quick Actions */}
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">Quick Actions</h5>
          <div className="d-flex gap-3 flex-wrap">
            <button
              className="btn btn-primary btn-admin"
              onClick={() => navigate('/admin/blogs/new')}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Create New Post
            </button>
            <button
              className="btn btn-primary btn-admin"
              onClick={() => navigate('/admin/comments')}
            >
              <i className="bi bi-chat-dots me-2"></i>
              View Pending Comments
            </button>
          </div>
        </div>
      </div>

      {/* Recent Posts */}
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">Recent Posts</h5>
        </div>
        <div className="card-body p-0">
          {recentPosts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-file-earmark-x fs-1 mb-3"></i>
              <p>No posts found. Create your first blog post!</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th>Likes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPosts.map(post => (
                    <tr key={post.id}>
                      <td>
                        <div className="fw-medium text-truncate" style={{ maxWidth: '300px' }}>
                          {post.title}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={post.status} type="blog" />
                      </td>
                      <td>
                        {post.updatedAt ? new Date(post.updatedAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          <i className="bi bi-heart me-1"></i>
                          {post.likes || 0}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => navigate(`/admin/blogs/${post.id}/edit`)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => navigate(`/blog/${post.slug}`)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Analytics Tab - Visible */}
      <div className="card analytics-section shadow-sm border-0 rounded-3 mt-4">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0"><i className="bi bi-bar-chart me-2"></i>Website Analytics</h5>
        </div>
        <div className="card-body">
          <AdminAnalytics />
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;